import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Send, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#FAF9F6]">
      <SEO
        title="Contact Our Editorial Team"
        description="Reach the Jewelry Refined editorial team with questions, story ideas, or partnership inquiries. We respond to authentication questions personally."
        url="/contact"
      />

      {/* Editorial Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-20 lg:py-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#111]">Contact</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <p className="overline overline-gold mb-6 animate-fade-in">Get In Touch</p>
              <h1 className="heading-editorial heading-editorial-lg text-[#111] mb-8 animate-fade-in-up">
                Write to <br />
                The <span className="italic">Editors</span>
              </h1>
              <p className="text-[#555] text-xl leading-relaxed font-light max-w-2xl">
                Authentication questions, story tips, and press inquiries — we read every
                message and respond personally within two business days.
              </p>
            </div>

            <div className="lg:col-span-4 lg:border-l border-[#E5E0D8] lg:pl-8">
              <p className="overline mb-3">Direct Line</p>
              <a
                href="mailto:hello@jewelryrefined.com"
                className="font-heading text-2xl text-[#111] hover:text-[#C69C6D] transition-colors inline-flex items-center gap-2"
              >
                hello@jewelryrefined.com
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <p className="overline mt-6 mb-2">Response Time</p>
              <p className="text-[#555]">Within 2 business days</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form + Sidebar */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              {submitted ? (
                <div className="border border-[#E5E0D8] p-12 lg:p-16 text-center">
                  <CheckCircle className="w-12 h-12 text-[#C69C6D] mx-auto mb-6" strokeWidth={1.2} />
                  <p className="overline overline-gold mb-3">Message Received</p>
                  <h3 className="heading-editorial text-3xl lg:text-4xl text-[#111] mb-4">
                    Thank you for <span className="italic">reaching out</span>
                  </h3>
                  <p className="text-[#555] leading-relaxed mb-8 max-w-md mx-auto">
                    We've received your message and will respond within two business days.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-editorial"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-10" data-testid="contact-form">
                  <p className="overline overline-gold">The Form</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
                    <div>
                      <label htmlFor="name" className="overline block mb-3">
                        Your Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="input-underline w-full text-[#111] placeholder:text-[#999]"
                        placeholder="Isabella Chen"
                        data-testid="contact-name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="overline block mb-3">
                        Email Address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="input-underline w-full text-[#111] placeholder:text-[#999]"
                        placeholder="you@example.com"
                        data-testid="contact-email"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="subject" className="overline block mb-3">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="input-underline w-full text-[#111] bg-transparent appearance-none cursor-pointer"
                        data-testid="contact-subject"
                      >
                        <option value="" disabled>Select a topic…</option>
                        <option value="Authentication Question">Authentication Question</option>
                        <option value="Buying Advice">Buying Advice</option>
                        <option value="Story Tip">Story Tip</option>
                        <option value="Press Inquiry">Press Inquiry</option>
                        <option value="Partnership">Partnership</option>
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="message" className="overline block mb-3">
                        Your Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        className="input-underline w-full text-[#111] placeholder:text-[#999] resize-none"
                        placeholder="Tell us what you're working on, the piece you're researching, or how we can help…"
                        data-testid="contact-message"
                      />
                    </div>
                  </div>

                  <div className="pt-6 border-t border-[#E5E0D8] flex items-center justify-between flex-wrap gap-4">
                    <p className="text-xs text-[#888] max-w-sm">
                      By submitting, you agree to our editorial response policy. We don't share
                      your details with third parties.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-editorial-inverse btn-editorial inline-flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="contact-submit"
                    >
                      {loading ? 'Sending…' : 'Send Message'}
                      {!loading && <Send className="w-3 h-3" strokeWidth={1.5} />}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4 lg:col-start-9">
              <div className="space-y-12">
                <div>
                  <p className="overline overline-gold mb-4">Editorial Office</p>
                  <h3 className="heading-editorial text-2xl text-[#111] mb-3">
                    For Story Tips & Authentication
                  </h3>
                  <p className="text-[#555] leading-relaxed">
                    Our authentication team personally reviews questions about specific pieces.
                    Include photographs, hallmarks, and context for the fastest reply.
                  </p>
                </div>

                <div className="border-t border-[#E5E0D8] pt-12">
                  <p className="overline overline-gold mb-4">Press & Partnerships</p>
                  <h3 className="heading-editorial text-2xl text-[#111] mb-3">
                    Working With Brands
                  </h3>
                  <p className="text-[#555] leading-relaxed mb-6">
                    We maintain editorial independence and disclose all paid relationships.
                    Partnership inquiries via the form above.
                  </p>
                  <Link
                    to="/affiliate-disclosure"
                    className="text-xs tracking-[0.15em] uppercase border-b border-[#111] pb-1 inline-flex items-center gap-2 hover:text-[#C69C6D] hover:border-[#C69C6D] transition-colors"
                  >
                    Editorial Standards
                    <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </Link>
                </div>

                <div className="bg-[#111] text-white p-8">
                  <Mail className="w-6 h-6 text-[#C69C6D] mb-4" strokeWidth={1.2} />
                  <p className="overline text-[#C69C6D] mb-3">Direct Email</p>
                  <a
                    href="mailto:hello@jewelryrefined.com"
                    className="font-heading text-xl text-white hover:text-[#C69C6D] transition-colors break-all"
                  >
                    hello@jewelryrefined.com
                  </a>
                  <p className="text-xs text-white/50 mt-4">
                    For urgent authentication queries, please email directly.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
